import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { SqlPreview } from "../../mapping/components/forms/SqlPreview";
import {
  DataQualityCheckResponse,
  DataQualityTable,
  checkDataMartDataQuality
} from "../../../services/dataMartModelling.service";
import { DqAttentionItem, buildDqAttentionItems, summarizeDqCheck } from "../dataQuality";
import { getApiErrorMessage } from "../../../helpers/apiError";

/**
 * Run › Data quality: what the data quality tests check for a data mart, and what needs attention.
 * Each finding carries the button that fixes it; the SQL is built when the user clicks Generate.
 * Nothing is created here: the tests reach the warehouse with the next generation run, and run
 * when a job with an active step for their plugin runs.
 */
export function DataQualityCheck({
  environmentId,
  dataMart,
  onOpenColumns,
  onIncludeTable
}: {
  environmentId: string | number;
  dataMart: { id: number; name: string } | null;
  onOpenColumns: (targetEntityDmId: number, filter: "unlinked" | "dim-key") => void;
  onIncludeTable: (targetEntityDmId: number) => Promise<void>;
}): JSX.Element {
  const [check, setCheck] = useState<DataQualityCheckResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isChecking, setIsChecking] = useState(false);
  const [busyItemId, setBusyItemId] = useState<string | null>(null);
  const [generatedSql, setGeneratedSql] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [copyStatus, setCopyStatus] = useState<"idle" | "copied" | "failed">("idle");
  const copyResetRef = useRef<number | undefined>(undefined);

  const runCheck = useCallback(async () => {
    if (!dataMart) {
      return;
    }
    setIsChecking(true);
    setError(null);
    setGeneratedSql(null);
    try {
      const response = await checkDataMartDataQuality(environmentId, {
        dataMartId: dataMart.id,
        dataMartName: dataMart.name,
        preview: true
      });
      setCheck(response);
    } catch (checkError) {
      setError(getApiErrorMessage(checkError, "Unable to check data quality for this data mart."));
    } finally {
      setIsChecking(false);
    }
  }, [dataMart, environmentId]);

  useEffect(() => {
    setCheck(null);
    void runCheck();
  }, [runCheck]);

  useEffect(
    () => () => {
      if (copyResetRef.current) {
        window.clearTimeout(copyResetRef.current);
      }
    },
    []
  );

  const summary = useMemo(() => (check ? summarizeDqCheck(check) : null), [check]);
  const items = useMemo(() => (check ? buildDqAttentionItems(check.dq_report) : []), [check]);
  const attention = items.filter((item) => item.action);
  const notes = items.filter((item) => !item.action);
  const facts = check?.dq_tables.filter((table) => table.kind === "fact") ?? [];
  const dimensions = check?.dq_tables.filter((table) => table.kind === "dimension") ?? [];

  const handleAction = async (item: DqAttentionItem) => {
    const action = item.action;
    if (!action) {
      return;
    }
    if (action.kind === "link-dimension") {
      onOpenColumns(action.entityId, "unlinked");
      return;
    }
    if (action.kind === "choose-key-column") {
      onOpenColumns(action.entityId, "dim-key");
      return;
    }
    setBusyItemId(item.id);
    try {
      await onIncludeTable(action.entityId);
      await runCheck();
    } catch (includeError) {
      setError(getApiErrorMessage(includeError, "Unable to include the table in data quality."));
    } finally {
      setBusyItemId(null);
    }
  };

  // The SQL is built only when asked for, like the Model SQL tab; the check itself creates nothing.
  const handleGenerateSql = async () => {
    if (!dataMart) {
      return;
    }
    setIsGenerating(true);
    setError(null);
    try {
      const response = await checkDataMartDataQuality(environmentId, {
        dataMartId: dataMart.id,
        dataMartName: dataMart.name,
        preview: true
      });
      setCheck(response);
      setGeneratedSql(response.sql ?? "");
    } catch (generateError) {
      setError(getApiErrorMessage(generateError, "Unable to generate the data quality SQL."));
    } finally {
      setIsGenerating(false);
    }
  };

  const sql = generatedSql ?? "";
  const handleCopySql = async () => {
    if (copyResetRef.current) {
      window.clearTimeout(copyResetRef.current);
    }
    try {
      if (typeof navigator === "undefined" || !navigator.clipboard) {
        throw new Error("Clipboard API unavailable");
      }
      await navigator.clipboard.writeText(sql);
      setCopyStatus("copied");
    } catch (copyError) {
      console.error("Unable to copy the data quality SQL", copyError);
      setCopyStatus("failed");
    } finally {
      copyResetRef.current = window.setTimeout(() => setCopyStatus("idle"), 2000);
    }
  };

  const schedule = check ? describeSchedule(check) : null;

  if (!dataMart) {
    return (
      <p className="tw-rounded-3xl tw-border tw-border-slate-200 tw-bg-slate-50 tw-p-4 tw-text-sm tw-text-slate-500">
        Select a data mart to check its data quality tests.
      </p>
    );
  }

  return (
    <div className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-rounded-3xl tw-border tw-border-slate-200 tw-bg-slate-50">
      <div className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-gap-3 tw-overflow-auto tw-p-4">
        {error ? (
          <p className="tw-mb-0 tw-rounded-2xl tw-bg-rose-50 tw-px-3 tw-py-2 tw-text-sm tw-font-medium tw-text-rose-700">
            {error}
          </p>
        ) : null}
        {isChecking && !check ? (
          <p className="tw-mb-0 tw-text-sm tw-text-slate-500">Checking what the tests will cover…</p>
        ) : null}

        {summary ? (
          <div className="tw-grid tw-grid-cols-2 tw-gap-3 md:tw-grid-cols-4">
            <SummaryCard label="Tests" value={String(summary.tests)} />
            <SummaryCard
              label="Tables tested"
              value={String(summary.tablesTested)}
              suffix={summary.tablesTotal > summary.tablesTested ? `of ${summary.tablesTotal}` : undefined}
            />
            <SummaryCard
              label="Keys checked against a dimension"
              value={String(summary.keysChecked)}
              suffix={summary.keysTotal > summary.keysChecked ? `of ${summary.keysTotal}` : undefined}
            />
            <SummaryCard
              label="Needs attention"
              value={String(summary.needsAttention)}
              tone={summary.needsAttention ? "warning" : "neutral"}
            />
          </div>
        ) : null}

        {attention.length ? (
          <Section title="Needs attention" meta={String(attention.length)} tone="warning">
            <div className="tw-flex tw-flex-col tw-gap-2">
              {attention.map((item) => (
                <div
                  key={item.id}
                  className={`tw-flex tw-items-center tw-gap-3 tw-rounded-2xl tw-border tw-px-4 tw-py-3 ${
                    item.severity === "warning"
                      ? "tw-border-amber-200 tw-bg-amber-50"
                      : "tw-border-slate-200 tw-bg-white"
                  }`}
                >
                  <div className="tw-min-w-0 tw-flex-1">
                    <p className="tw-mb-0 tw-text-sm tw-font-medium tw-text-slate-800">{item.title}</p>
                    <p
                      className={`tw-mb-0 tw-text-xs ${item.severity === "warning" ? "tw-text-amber-800" : "tw-text-slate-500"}`}
                    >
                      {item.detail}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => void handleAction(item)}
                    disabled={busyItemId === item.id || isChecking}
                    className="tw-shrink-0 tw-rounded-full tw-border tw-border-[#03234a] tw-bg-[#03234a] tw-px-3 tw-py-1.5 tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-white hover:tw-bg-[#021b39] disabled:tw-cursor-not-allowed disabled:tw-opacity-60"
                  >
                    {item.action?.kind === "link-dimension"
                      ? "Link dimension"
                      : item.action?.kind === "choose-key-column"
                        ? "Choose key column"
                        : busyItemId === item.id
                          ? "Including…"
                          : "Include"}
                  </button>
                </div>
              ))}
            </div>
          </Section>
        ) : null}

        {check && check.dq_tables.length ? (
          <Section
            title="What gets tested"
            meta={`${check.dq_tables.length} tables · ${check.tests} tests`}
          >
            <p className="tw-mb-2 tw-text-xs tw-text-slate-500">
              Every table: its rows are unique and no key is empty. Facts also: every linked key has a
              row in its dimension.
            </p>
            {facts.length ? (
              <TableGroup label={`Facts (${facts.length})`}>
                {facts.map((fact) => (
                  <FactRow key={fact.entity_id} fact={fact} />
                ))}
              </TableGroup>
            ) : null}
            {dimensions.length ? (
              <TableGroup label={`Dimensions (${dimensions.length})`}>
                {dimensions.map((dimension) => (
                  <DimensionRow key={dimension.entity_id} dimension={dimension} />
                ))}
              </TableGroup>
            ) : null}
          </Section>
        ) : null}

        {notes.length ? (
          <Section title="Notes" meta={String(notes.length)}>
            <ul className="tw-mb-0 tw-list-disc tw-pl-5 tw-text-xs tw-text-slate-600">
              {notes.map((item) => (
                <li key={item.id}>
                  {item.title}
                  {item.detail ? `. ${item.detail}` : ""}
                </li>
              ))}
            </ul>
          </Section>
        ) : null}

        {check && !isUnsupported(check) ? (
          <Section
            title="SQL"
            meta={check.stored_procedure}
            defaultOpen
            actions={
              <div className="tw-flex tw-items-center tw-gap-2">
                <button
                  type="button"
                  onClick={() => void handleGenerateSql()}
                  disabled={isGenerating || isChecking}
                  className="tw-rounded-full tw-border tw-border-[#03234a] tw-bg-[#03234a] tw-px-3 tw-py-1 tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-white hover:tw-bg-[#021b39] disabled:tw-cursor-not-allowed disabled:tw-opacity-60"
                >
                  {isGenerating ? "Generating…" : "Generate"}
                </button>
                <button
                  type="button"
                  onClick={() => void handleCopySql()}
                  disabled={!sql || isGenerating}
                  className="tw-rounded-full tw-border tw-border-slate-200 tw-bg-white tw-px-3 tw-py-1 tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-[#03234a] hover:tw-border-slate-400 disabled:tw-cursor-not-allowed disabled:tw-opacity-60"
                >
                  {copyStatus === "copied" ? "Copied" : copyStatus === "failed" ? "Copy failed" : "Copy"}
                </button>
              </div>
            }
          >
            {sql ? (
              <div className="tw-max-h-[60vh] tw-overflow-auto tw-rounded-2xl tw-border tw-border-slate-200 tw-bg-white tw-p-4">
                <SqlPreview value={sql} wrap showLineNumbers />
              </div>
            ) : (
              <p className="tw-mb-0 tw-rounded-2xl tw-border tw-border-dashed tw-border-slate-200 tw-bg-white tw-px-4 tw-py-3 tw-text-xs tw-text-slate-500">
                Generate to see the procedure these tests run as. Nothing is created or deployed.
              </p>
            )}
          </Section>
        ) : null}
      </div>

      <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3 tw-border-t tw-border-slate-200 tw-px-4 tw-py-3">
        <div className="tw-min-w-0 tw-flex-1 tw-text-xs">
          <p
            className={`tw-mb-0 ${
              schedule?.tone === "warning"
                ? "tw-text-amber-800"
                : schedule?.tone === "ok"
                  ? "tw-text-emerald-800"
                  : "tw-text-slate-700"
            }`}
          >
            {schedule?.text}
          </p>
          {schedule?.generated ? (
            <p className="tw-mb-0 tw-text-slate-500">
              Changes reach the warehouse with the next generation run of this data mart.
            </p>
          ) : null}
        </div>
        <button
          type="button"
          onClick={() => void runCheck()}
          disabled={isChecking}
          className="tw-rounded-full tw-border tw-border-slate-200 tw-bg-white tw-px-3 tw-py-1.5 tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-[#03234a] hover:tw-border-slate-400 disabled:tw-opacity-60"
        >
          {isChecking ? "Checking…" : "Check again"}
        </button>
      </div>
    </div>
  );
}

const unique = (values: string[]) => values.filter((value, index) => values.indexOf(value) === index);
const quoted = (name: string) => `'${name}'`;
const isUnsupported = (check: Pick<DataQualityCheckResponse, "dq_report">) =>
  check.dq_report.some((entry) => entry.kind === "unsupported_platform");

/**
 * One sentence on whether the tests run, and if not, the one thing that stops them. The job queue
 * runs a plugin's step only when the plugin, the job, the task group and the task are all switched
 * on, and skips it without a trace otherwise, which is why it gets said here.
 */
export const describeSchedule = (
  check: Pick<DataQualityCheckResponse, "plugin" | "plugin_state" | "dq_report">
): { text: string; tone: "ok" | "warning" | "neutral"; generated: boolean } => {
  const unsupported = check.dq_report.find((entry) => entry.kind === "unsupported_platform");
  if (unsupported) {
    return { text: `No data quality tests: ${unsupported.detail}.`, tone: "neutral", generated: false };
  }
  const { plugin, plugin_state: state } = check;
  if (!state.exists) {
    return {
      text: `Not run: ${quoted(plugin)} is created by the next generation run. Then add it to a job.`,
      tone: "neutral",
      generated: true
    };
  }
  if (!state.jobs.length) {
    return {
      text: `Not run: no job has a step for ${quoted(plugin)}. Add one to run these tests with a load.`,
      tone: "neutral",
      generated: true
    };
  }
  const running = unique(state.jobs.filter((job) => job.runs).map((job) => job.job_name));
  if (running.length) {
    const names = running.map(quoted).join(", ");
    return {
      text: `Included in ${running.length > 1 ? "jobs" : "job"} ${names}.`,
      tone: "ok",
      generated: true
    };
  }
  if (!state.is_active) {
    return {
      text: `Not run: ${quoted(plugin)} is switched off under Custom plugins.`,
      tone: "warning",
      generated: true
    };
  }
  const step = state.jobs[0];
  const what =
    step.switched_off[0] === "job"
      ? `the job ${quoted(step.job_name)} is switched off`
      : step.switched_off[0] === "task group"
        ? `its task group ${quoted(step.job_task_group_name)} in job ${quoted(step.job_name)} is switched off`
        : `its step ${quoted(step.job_task_name)} in job ${quoted(step.job_name)} is switched off`;
  return { text: `Not run: ${what}.`, tone: "warning", generated: true };
};

/** A section that folds away; `actions` sit in the header without toggling it. */
const Section = ({
  title,
  meta,
  defaultOpen = false,
  tone = "neutral",
  actions,
  children
}: {
  title: string;
  meta?: string;
  defaultOpen?: boolean;
  tone?: "neutral" | "warning";
  actions?: React.ReactNode;
  children: React.ReactNode;
}) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section aria-label={title} className="tw-flex tw-flex-col tw-gap-2">
      <div className="tw-flex tw-items-center tw-gap-3">
        <button
          type="button"
          aria-expanded={open}
          onClick={() => setOpen((value) => !value)}
          className="tw-flex tw-min-w-0 tw-flex-1 tw-items-center tw-gap-2 tw-rounded-md tw-border-0 tw-bg-transparent tw-p-0 tw-text-left tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-slate-300"
        >
          <span
            aria-hidden
            className={`tw-inline-block tw-text-[10px] tw-text-slate-400 tw-transition-transform ${open ? "tw-rotate-90" : ""}`}
          >
            ▶
          </span>
          <span
            className={`tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide ${
              tone === "warning" ? "tw-text-amber-800" : "tw-text-slate-500"
            }`}
          >
            {title}
          </span>
          {meta ? <span className="tw-truncate tw-text-xs tw-text-slate-400">{meta}</span> : null}
        </button>
        {actions}
      </div>
      {open ? children : null}
    </section>
  );
};

const TableGroup = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="tw-mb-2 tw-overflow-hidden tw-rounded-2xl tw-border tw-border-slate-200 tw-bg-white">
    <p className="tw-mb-0 tw-bg-slate-50 tw-px-4 tw-py-1.5 tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-500">
      {label}
    </p>
    <div className="tw-divide-y tw-divide-slate-100">{children}</div>
  </div>
);

const schemaOf = (qualified: string) => qualified.slice(0, Math.max(qualified.indexOf("."), 0));
const nameOf = (qualified: string) => qualified.slice(qualified.indexOf(".") + 1);

/** A table's own name in code type, its schema quiet beside it; neither ever breaks mid-word. */
const TableName = ({ qualified }: { qualified: string }) => (
  <span className="tw-flex tw-min-w-0 tw-items-baseline tw-gap-2" title={qualified}>
    <span className="tw-truncate tw-whitespace-nowrap tw-break-normal tw-font-mono tw-text-sm tw-text-slate-800">
      {nameOf(qualified)}
    </span>
    <span className="tw-truncate tw-whitespace-nowrap tw-break-normal tw-text-xs tw-text-slate-400">
      {schemaOf(qualified)}
    </span>
  </span>
);

const Filter = ({ filter }: { filter: string | null }) =>
  filter ? (
    <span
      title={filter}
      className="tw-max-w-[16rem] tw-truncate tw-whitespace-nowrap tw-rounded-full tw-bg-slate-100 tw-px-2 tw-py-0.5 tw-font-mono tw-text-[11px] tw-text-slate-600"
    >
      {filter}
    </span>
  ) : null;

const Tests = ({ count }: { count: number }) => (
  <span className="tw-shrink-0 tw-whitespace-nowrap tw-text-xs tw-text-slate-500">{count} tests</span>
);

const FactRow = ({ fact }: { fact: DataQualityTable }) => {
  const factSchema = schemaOf(fact.table);
  return (
    <div className="tw-px-4 tw-py-2.5">
      <div className="tw-flex tw-items-center tw-gap-3">
        <div className="tw-min-w-0 tw-flex-1">
          <TableName qualified={fact.table} />
        </div>
        <Filter filter={fact.filter} />
        <Tests count={fact.tests} />
      </div>
      {fact.keys.length ? (
        <div className="tw-mt-1.5 tw-grid tw-grid-cols-[minmax(0,max-content)_auto_minmax(0,1fr)] tw-gap-x-2 tw-gap-y-0.5 tw-pl-3 tw-text-xs">
          {fact.keys.map((key) => (
            <React.Fragment key={key.column_id ?? key.column}>
              <span className="tw-truncate tw-whitespace-nowrap tw-font-mono tw-text-slate-700" title={key.column}>
                {key.column}
              </span>
              <span aria-hidden className="tw-text-slate-400">
                →
              </span>
              <span className="tw-truncate tw-whitespace-nowrap" title={key.dimension}>
                <span className="tw-font-mono tw-text-slate-700">{nameOf(key.dimension)}</span>
                {schemaOf(key.dimension) !== factSchema ? (
                  <span className="tw-ml-1.5 tw-text-slate-400">{schemaOf(key.dimension)}</span>
                ) : null}
              </span>
            </React.Fragment>
          ))}
        </div>
      ) : (
        <p className="tw-mb-0 tw-mt-1 tw-pl-3 tw-text-xs tw-text-slate-400">No keys linked to a dimension.</p>
      )}
    </div>
  );
};

const DimensionRow = ({ dimension }: { dimension: DataQualityTable }) => (
  <div className="tw-flex tw-items-center tw-gap-3 tw-px-4 tw-py-2">
    <div className="tw-min-w-0 tw-flex-1">
      <TableName qualified={dimension.table} />
    </div>
    <span
      className="tw-hidden tw-max-w-[22rem] tw-truncate tw-whitespace-nowrap tw-text-xs tw-text-slate-500 md:tw-inline"
      title={dimension.key_column}
    >
      key <span className="tw-font-mono tw-text-slate-700">{dimension.key_column}</span>
    </span>
    <Filter filter={dimension.filter} />
    <Tests count={dimension.tests} />
  </div>
);

const SummaryCard = ({
  label,
  value,
  suffix,
  tone = "neutral"
}: {
  label: string;
  value: string;
  suffix?: string;
  tone?: "neutral" | "warning";
}) => (
  <div
    className={`tw-rounded-2xl tw-border tw-px-4 tw-py-2.5 ${
      tone === "warning" ? "tw-border-amber-200 tw-bg-amber-50" : "tw-border-slate-200 tw-bg-white"
    }`}
  >
    <p className={`tw-mb-0 tw-text-xs ${tone === "warning" ? "tw-text-amber-800" : "tw-text-slate-500"}`}>{label}</p>
    <p className={`tw-mb-0 tw-text-xl tw-font-semibold ${tone === "warning" ? "tw-text-amber-800" : "tw-text-slate-900"}`}>
      {value}
      {suffix ? <span className="tw-ml-1 tw-text-sm tw-font-normal tw-text-slate-500">{suffix}</span> : null}
    </p>
  </div>
);
